// The Events editor's Email Performance data, rebuilt from a bar-event-email-stats reply.
//
// The editor renders every field as a React child or does arithmetic on it, so a field
// that is not what it claims (an object, a string, an object whose toString throws)
// throws mid-render and takes the whole editor down. Verifiers R4 and R5 each found one
// more such field, so nothing from the server is passed through: every value below is
// rebuilt from a checked primitive. Off-shape stats or tracking hide the panel (null),
// because "0 opened" is exactly the believable lie the panel exists to stop.

export type Tracking = "ok" | "none" | "incomplete" | "not_reporting";
export type ReminderCounts = { pending: number; sent: number; skipped: number };

export interface EmailStats {
  /** `sent` is the audit-log total; rates are over `tracked` (sends with a Postmark id). */
  stats: { sent: number; tracked: number; opened: number; clicked: number; open_rate: number; click_rate: number };
  reminders: Record<"reminder_24h" | "reminder_6h", ReminderCounts>;
  send_history: Array<{ sent_at: string; email_sent: number; sms_sent: number }>;
  /** Whether opens/clicks can be trusted — see the function's core.ts. */
  tracking: Tracking;
}

/** Date.parse("12") is 2012, so a timestamp must at least look like one. */
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const TRACKING: readonly string[] = ["ok", "none", "incomplete", "not_reporting"];

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const num = (v: unknown): number => (isNum(v) ? v : 0);
const obj = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

function reminder(v: unknown): ReminderCounts {
  const r = obj(v);
  return { pending: num(r?.pending), sent: num(r?.sent), skipped: num(r?.skipped) };
}

export function normalizeEmailStats(raw: unknown): EmailStats | null {
  const res = obj(raw);
  if (!res || res.ok !== true) return null;
  const s = obj(res.stats);
  if (!s) return null;
  const keys = ["sent", "tracked", "opened", "clicked", "open_rate", "click_rate"] as const;
  if (!keys.every((k) => isNum(s[k]))) return null;
  if (typeof res.tracking !== "string" || !TRACKING.includes(res.tracking)) return null;

  const rem = obj(res.reminders);
  const history = (Array.isArray(res.send_history) ? res.send_history : [])
    .map(obj)
    .filter((h): h is Record<string, unknown> =>
      !!h && typeof h.sent_at === "string" && ISO_RE.test(h.sent_at) && !Number.isNaN(Date.parse(h.sent_at)))
    .map((h) => ({ sent_at: h.sent_at as string, email_sent: num(h.email_sent), sms_sent: num(h.sms_sent) }));

  return {
    stats: {
      sent: s.sent as number,
      tracked: s.tracked as number,
      opened: s.opened as number,
      clicked: s.clicked as number,
      open_rate: s.open_rate as number,
      click_rate: s.click_rate as number,
    },
    reminders: { reminder_24h: reminder(rem?.reminder_24h), reminder_6h: reminder(rem?.reminder_6h) },
    send_history: history,
    tracking: res.tracking as Tracking,
  };
}
